import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
export type LegalDoc = 'privacidad' | 'terminos' | null;
interface LegalModalProps {
  open: LegalDoc;
  onClose: () => void;
}
const content: Record<Exclude<LegalDoc, null>, {
  title: string;
  sections: {
    heading: string;
    body: string;
  }[];
}> = {
  privacidad: {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Compromiso con tu privacidad',
      body: 'En Affiliate Wave entendemos que la privacidad no es un lujo, es una necesidad. Toda la información que recopilamos se utiliza únicamente para entregarte acceso a la comunidad, los entrenamientos y los recursos contratados. Nunca compartimos, vendemos ni cedemos tus datos a terceros con fines comerciales.'
    }, {
      heading: '2. Información que recopilamos',
      body: 'Recopilamos únicamente la información estrictamente necesaria para crear y mantener tu cuenta: nombre o seudónimo, correo electrónico y datos básicos de facturación cuando adquieres un plan. No requerimos documentos de identidad, número de seguro social, ni información migratoria de ningún tipo.'
    }, {
      heading: '3. Cómo usamos tu información',
      body: 'Tus datos se usan para darte acceso al contenido, enviarte comunicaciones relevantes sobre tu cuenta y mejorar tu experiencia dentro de la comunidad. No utilizamos tu información para perfilarte públicamente ni para mostrar tu identidad en ningún espacio.'
    }, {
      heading: '4. Seudónimos y operación anónima',
      body: 'Puedes participar en la comunidad bajo un seudónimo o nombre comercial. Nuestra plataforma está diseñada para que operes con la mayor discreción posible y mantengas tu identidad protegida en todo momento.'
    }, {
      heading: '5. Seguridad de los datos',
      body: 'Aplicamos medidas técnicas y organizativas razonables para proteger tu información frente a accesos no autorizados, pérdida o uso indebido. Los pagos se procesan a través de proveedores externos certificados y nunca almacenamos información completa de tarjetas en nuestros sistemas.'
    }, {
      heading: '6. Tus derechos',
      body: 'Puedes solicitar en cualquier momento acceder, corregir o eliminar la información personal que tenemos sobre ti, así como cerrar tu cuenta. Estas solicitudes se atienden con prioridad y absoluta confidencialidad.'
    }, {
      heading: '7. Cambios a esta política',
      body: 'Podemos actualizar esta política para reflejar mejoras en nuestros procesos o cambios legales. Cualquier modificación se publicará en esta misma página con la fecha de la última actualización.'
    }]
  },
  terminos: {
    title: 'Términos y Condiciones',
    sections: [{
      heading: '1. Aceptación de los términos',
      body: 'Al acceder, registrarte o utilizar Affiliate Wave aceptas estos Términos y Condiciones en su totalidad. Si no estás de acuerdo con alguna parte, debes abstenerte de usar la plataforma y los recursos asociados.'
    }, {
      heading: '2. Naturaleza del servicio',
      body: 'Affiliate Wave es una comunidad educativa enfocada en marketing de afiliados. Proveemos entrenamientos, recursos, plantillas y un espacio comunitario. No somos una oferta de empleo, no garantizamos ingresos específicos ni ofrecemos asesoría migratoria, legal, fiscal o financiera.'
    }, {
      heading: '3. Resultados y expectativas',
      body: 'Los resultados que obtengas dependen de tu dedicación, tiempo invertido, ejecución y condiciones del mercado. Los ejemplos, capturas o cifras mostradas son referenciales y no constituyen una promesa de ingresos. El marketing de afiliados requiere trabajo constante y profesional.'
    }, {
      heading: '4. Uso permitido',
      body: 'Te comprometes a usar la plataforma de manera ética y legal. Está prohibido compartir, redistribuir, revender o publicar los materiales formativos fuera de la comunidad sin autorización expresa por escrito. Cualquier uso indebido podrá derivar en la suspensión inmediata de tu acceso sin reembolso.'
    }, {
      heading: '5. Propiedad intelectual',
      body: 'Todos los contenidos, videos, textos, plantillas y materiales de Affiliate Wave son propiedad de sus creadores y están protegidos por las leyes de propiedad intelectual aplicables. Tu acceso te otorga una licencia personal e intransferible de uso, no la propiedad sobre el material.'
    }, {
      heading: '6. Pagos y acceso',
      body: 'Los planes pagos otorgan acceso bajo las condiciones publicadas al momento de la compra. Cualquier política de reembolso, renovación o cancelación se rige por las condiciones específicas de cada plan y por la plataforma que procesa el pago.'
    }, {
      heading: '7. Conducta dentro de la comunidad',
      body: 'Mantén una conducta respetuosa con el resto de miembros. No se tolerará acoso, discriminación, spam, promoción de actividades ilegales o cualquier comportamiento que ponga en riesgo a la comunidad. Nos reservamos el derecho de remover usuarios que infrinjan estas normas.'
    }, {
      heading: '8. Limitación de responsabilidad',
      body: 'Affiliate Wave y sus creadores no se hacen responsables por decisiones comerciales, financieras o personales que tomes a partir del contenido. Eres el único responsable de cumplir con la normativa que aplique en tu país o ciudad de residencia.'
    }, {
      heading: '9. Modificaciones',
      body: 'Podemos actualizar estos términos en cualquier momento. La versión vigente será siempre la publicada en esta página. El uso continuado de la plataforma después de un cambio implica la aceptación de la nueva versión.'
    }]
  }
};
export function LegalModal({
  open,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);
  const doc = open ? content[open] : null;
  return <AnimatePresence data-id="element-108">
      {open && doc && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-109">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-110" />

          {/* Panel */}
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative z-10 w-full max-w-2xl max-h-[85vh] flex flex-col bg-[#111] border border-[#d4a437]/30 rounded-2xl shadow-2xl overflow-hidden" data-id="element-111">
            {/* Header */}
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/10 flex-shrink-0" data-id="element-112">
              <h2 id="legal-modal-title" className="font-serif text-xl sm:text-2xl font-semibold text-[#d4a437]" data-id="element-113">
                {doc.title}
              </h2>
              <button onClick={onClose} aria-label="Cerrar" className="flex items-center justify-center w-9 h-9 rounded-md text-neutral-400 hover:text-white hover:bg-white/5 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#d4a437]" data-id="element-114">
                <X className="w-5 h-5" data-id="element-115" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto px-6 sm:px-8 py-6 space-y-6" data-id="element-116">
              {doc.sections.map(section => <section key={section.heading} data-id="element-117">
                  <h3 className="text-base font-semibold text-white mb-2" data-id="element-118">
                    {section.heading}
                  </h3>
                  <p className="text-sm sm:text-base text-neutral-400 leading-relaxed" data-id="element-119">
                    {section.body}
                  </p>
                </section>)}
              <p className="text-xs text-neutral-600 pt-4 border-t border-white/5" data-id="element-120">
                Última actualización:{' '}
                {new Date().toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 sm:px-8 py-4 border-t border-white/10 flex justify-end flex-shrink-0" data-id="element-121">
              <button onClick={onClose} className="inline-flex h-10 items-center justify-center rounded-md bg-[#d4a437] px-5 text-sm font-medium text-[#0a0a0a] transition-colors hover:bg-[#b88c2e] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#d4a437]" data-id="element-122">
                Entendido
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}